/**
 * Invoice Modal - Professionelle Rechnungsübersicht mit Live-Bearbeitung
 * Responsive Design für Mobile & Desktop
 * Features: Mietbeginn/Ende ändern, Artikel hinzufügen/ändern/löschen, Live-Preisberechnung
 * Unterstützt Kleinunternehmerregelung
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Slide,
  Badge,
  Tooltip,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Container,
  Stack,
  Avatar,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  AccessTime as AccessTimeIcon,
  LocalOffer as LocalOfferIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Calculate as CalculateIcon,
  ShoppingCart as ShoppingCartIcon,
  Euro as EuroIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  TouchApp as TouchAppIcon,
} from "@mui/icons-material";
import EndRentalModal from "./EndRentalModal";
import {
  Rental,
  Article,
  RentalItem,
  Company,
  RentalObjectBundle,
} from "../../types/api-types";
import { apiClient } from "../../services/apiClient";

interface InvoiceModalProps {
  rental: Rental;
  rentalObjectId?: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface InvoiceCalculation {
  rentalHours: number;
  rentalPrice: number;
  articlesPrice: number;
  subtotal: number;
  tax: number;
  total: number;
  isSmallBusiness: boolean;
  vatRate: number;
  effectiveRate?: number;
  timeUnit?: string;
  billedQuantity?: number;
  wasMinimumApplied?: boolean;
  rentalInterval?: number;
  minimumRentalDuration?: number;
  currentDurationMinutes?: number;
}

interface MobileStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  completed: boolean;
  hasChanges?: boolean;
}

// Mobile Navigation Tabs
const MOBILE_TABS = [
  { id: "summary", label: "Übersicht", icon: <CalculateIcon /> },
  { id: "time", label: "Zeit", icon: <AccessTimeIcon /> },
  { id: "bundle", label: "Bundle", icon: <BusinessIcon /> },
  { id: "items", label: "Artikel", icon: <InventoryIcon /> },
];

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  rental,
  rentalObjectId,
  onClose,
  onUpdate,
}) => {
  const [currentRental, setCurrentRental] = useState<Rental>(rental);
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState<InvoiceCalculation | null>(
    null
  );
  const [primaryCompany, setPrimaryCompany] = useState<Company | null>(null);

  // Mobile/Responsive States
  const [activeTab, setActiveTab] = useState("summary");
  const [isQuickEditMode, setIsQuickEditMode] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Edit States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasStartDateChanged, setHasStartDateChanged] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);

  // Article Management
  const [availableArticles, setAvailableArticles] = useState<Article[]>([]);
  const [showAddArticles, setShowAddArticles] = useState(false);
  const [selectedNewArticles, setSelectedNewArticles] = useState<{
    [key: string]: number;
  }>({});
  const [editingTimeItem, setEditingTimeItem] = useState<RentalItem | null>(
    null
  );
  const [editingTimeRange, setEditingTimeRange] = useState({
    startedAt: "",
    endedAt: "",
  });

  // Bundle Management
  const [availableBundles, setAvailableBundles] = useState<
    RentalObjectBundle[]
  >([]);
  const [showBundleSelection, setShowBundleSelection] = useState(false);

  // End Rental Modal
  const [showEndRentalModal, setShowEndRentalModal] = useState(false);
  const [endRentalResponse, setEndRentalResponse] = useState<any>(null);

  // Ref für automatisches Speichern der Startzeit
  const updateStartDateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Theme & Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    initializeData();
    loadAvailableArticles();
    loadAvailableBundles(); // Lade verfügbare Bundles
    loadCompanySettings();
    loadCurrentRentalWithItems(); // Lade aktuelle Rental-Daten mit Items

    // Cleanup function für Timer
    return () => {
      if (updateStartDateTimeoutRef.current) {
        clearTimeout(updateStartDateTimeoutRef.current);
      }
    };
  }, []);

  // Separate useEffect für Bundle-Artikel wenn sich currentRental ändert
  useEffect(() => {
    if (currentRental) {
      loadAvailableBundles();
    }
  }, [currentRental]);

  useEffect(() => {
    calculatePrices();
  }, [currentRental, startDate, endDate, primaryCompany]);

  const loadCompanySettings = async () => {
    try {
      const companies = await apiClient.getCompanies();
      const primary =
        companies.find((c) => c.isPrimary) || companies[0] || null;
      setPrimaryCompany(primary);
    } catch (error) {
      console.error("Failed to load company settings:", error);
    }
  };

  const loadCurrentRentalWithItems = async () => {
    try {
      console.log("🔄 Loading current rental with items from backend...");
      // Lade die aktuellen Rental-Daten inklusive Items vom Backend
      const updatedRental = await apiClient.getRental(currentRental.id);
      console.log("✅ Updated rental data:", updatedRental);
      console.log("🔍 Object in rental:", updatedRental?.object);
      console.log(
        "🔍 Bundle articles in object:",
        updatedRental?.object?.bundleArticles
      );

      // Aktualisiere den currentRental State mit den Backend-Daten
      setCurrentRental(updatedRental);

      // Nach dem Laden der Rental-Daten, lade auch die Bundle-Artikel neu
      // um sicherzustellen, dass die neuesten Bundle-Daten verfügbar sind
      setTimeout(() => {
        loadAvailableBundles();
      }, 100); // Kurze Verzögerung um sicherzustellen, dass currentRental aktualisiert ist
    } catch (error) {
      console.error("❌ Failed to load current rental with items:", error);
      // Falls das Laden fehlschlägt, verwenden wir die ursprünglichen Daten
    }
  };

  const initializeData = () => {
    if (!currentRental) return;

    const start = new Date(currentRental.startDate);
    const end = currentRental.endDate
      ? new Date(currentRental.endDate)
      : new Date();

    setStartDate(formatDateTimeLocal(start));
    setEndDate(formatDateTimeLocal(end));
  };

  const loadAvailableArticles = async () => {
    try {
      const articles = await apiClient.getArticles({ isActive: true });
      setAvailableArticles(Array.isArray(articles) ? articles : []);
    } catch (error) {
      console.error("Failed to load articles:", error);
    }
  };

  const loadAvailableBundles = async () => {
    try {
      console.log(
        "🔍 loadAvailableBundles called with currentRental:",
        currentRental
      );
      console.log("🔍 Original rental prop:", rental);

      // Prüfe zuerst, ob das aktuelle Rental Object bereits Bundle-Artikel hat
      if (currentRental?.object?.bundleArticles) {
        console.log(
          "✅ Using bundle articles from rental object:",
          currentRental.object.bundleArticles
        );
        setAvailableBundles(currentRental.object.bundleArticles);
        return;
      }

      // Fallback: Lade Bundle-Artikel über API
      // Versuche sowohl objectId als auch object.id, und auch vom ursprünglichen rental prop
      // und die neue rentalObjectId Property
      const objectId =
        currentRental?.objectId ||
        currentRental?.object?.id ||
        rental?.objectId ||
        rental?.object?.id ||
        rentalObjectId; // Neue Property verwenden

      if (!objectId) {
        console.log("❌ No objectId available for loading bundles");
        console.log(
          "❌ currentRental properties:",
          currentRental ? Object.keys(currentRental) : "no rental"
        );
        console.log(
          "❌ original rental properties:",
          rental ? Object.keys(rental) : "no rental"
        );
        console.log("❌ rentalObjectId prop:", rentalObjectId);
        setAvailableBundles([]);
        return;
      }

      console.log("🔄 Loading available bundles for object:", objectId);

      // Lade Bundle-Artikel für das aktuelle Rental Object
      const response = await apiClient.getBundleArticlesForObject(objectId);
      console.log("✅ Available bundles API response:", response);

      // Die API gibt die Daten in einem `data` Array zurück
      const bundles = (response as any)?.data || response || [];
      console.log("✅ Extracted bundles:", bundles);

      setAvailableBundles(Array.isArray(bundles) ? bundles : []);
    } catch (error) {
      console.error("Failed to load bundles:", error);
      setAvailableBundles([]); // Leere Liste bei Fehler
    }
  };

  const calculatePrices = async () => {
    if (!currentRental) return;

    try {
      setLoading(true);

      // Erstelle Preview-Endzeit basierend auf aktueller UI-Eingabe
      const previewEndDate = endDate ? new Date(endDate) : undefined;

      // Hole Live-Preisberechnung vom Backend mit optionaler Endzeit
      const response = await apiClient.getRentalLivePrice(
        currentRental.id,
        previewEndDate
      );
      console.log("✅ Live price response:", response);
      console.log("🔍 Response type:", typeof response);
      console.log(
        "🔍 Response keys:",
        response ? Object.keys(response) : "no response"
      );

      if (!response || typeof response !== "object") {
        console.error("❌ No valid response data received");
        throw new Error("Keine Preisdaten vom Server erhalten");
      }

      // Parse priceBreakdown für Anzeige-Details
      let breakdown = null;
      try {
        breakdown = JSON.parse(response.priceBreakdown || "{}");
      } catch (e) {
        console.error("❌ Fehler beim Parsen des Preis-Breakdowns", e);
        breakdown = {};
      }

      console.log("📊 Parsed breakdown:", breakdown);

      // Objekt-Daten für Mindestmietdauer und Intervall abrufen
      const objData = currentRental.object;
      const minimumRentalDuration = objData?.minimumRentalDuration || 0.5;
      const rentalInterval = objData?.rentalInterval || 0.25;
      const rentalUnit = objData?.rentalUnit || "HOURS";

      // ✅ Verwende die EXAKTEN Backend-Response-Daten
      const currentDurationMinutes = response.currentDurationMinutes || 0;
      const baseAmount = response.baseAmount || 0;
      const itemsTotal = response.itemsTotal || 0;
      const currentTotal = response.currentTotal || 0;

      console.log("� Backend API Response details:", {
        response,
        hasResponse: !!response,
        responseKeys: response ? Object.keys(response) : [],
        currentDurationMinutes,
        baseAmount,
        itemsTotal,
        currentTotal,
        breakdown,
      });

      // ✅ Berechne Mietpreis korrekt mit rate × quantity aus breakdown
      let rentalPriceBrutto = baseAmount; // Fallback auf Backend-Wert

      // Prüfe ob breakdown verfügbar ist und berechne mit rate × quantity
      if (breakdown?.rate && breakdown?.quantity) {
        const calculatedRentalPrice = breakdown.rate * breakdown.quantity;
        console.log("💰 Calculating rental price:", {
          rate: breakdown.rate,
          quantity: breakdown.quantity,
          calculated: calculatedRentalPrice,
          backendBase: baseAmount,
        });
        rentalPriceBrutto = calculatedRentalPrice;
      } else {
        console.log("⚠️ Using backend baseAmount as fallback:", baseAmount);
      }

      const articlesPriceBrutto = itemsTotal; // Zusatzartikel-Preis
      const totalBrutto = currentTotal; // Gesamtpreis

      console.log("💰 Using calculated prices:", {
        rentalPriceBrutto,
        articlesPriceBrutto,
        totalBrutto,
      });

      // ✅ MwSt.-Berechnung basierend auf Backend-Preisen
      const isSmallBusiness = primaryCompany?.isSmallBusiness || false;
      const vatRate = isSmallBusiness
        ? 0
        : primaryCompany?.defaultVatRate || 19;

      let rentalPrice, articlesPrice, subtotal, tax, total;

      if (isSmallBusiness) {
        // Kleinunternehmerregelung: Backend-Preise sind bereits netto
        rentalPrice = rentalPriceBrutto;
        articlesPrice = articlesPriceBrutto;
        total = totalBrutto;
        subtotal = total;
        tax = 0;
      } else {
        // Normale MwSt.: Backend könnte brutto oder netto liefern
        // Wir nehmen an, dass Backend brutto liefert
        rentalPrice = rentalPriceBrutto;
        articlesPrice = articlesPriceBrutto;
        total = totalBrutto;

        // MwSt. herausrechnen für Anzeige
        const vatMultiplier = 1 + vatRate / 100;
        subtotal = total / vatMultiplier;
        tax = total - subtotal;
      }

      console.log("🧾 MwSt calculation:", {
        isSmallBusiness,
        vatRate,
        rentalPrice,
        articlesPrice,
        subtotal,
        tax,
        total,
      });

      setCalculation({
        rentalHours: currentDurationMinutes / 60,
        rentalPrice,
        articlesPrice,
        subtotal,
        tax,
        total,
        isSmallBusiness,
        vatRate,
        // ✅ Backend-Breakdown-Daten verwenden
        effectiveRate: breakdown?.rate || 0,
        timeUnit: breakdown?.timeUnit || "Stunden",
        billedQuantity: breakdown?.quantity || 0,
        wasMinimumApplied: breakdown?.includesMinimum || false,
        rentalInterval,
        minimumRentalDuration,
        currentDurationMinutes: currentDurationMinutes,
      });

      console.log("✅ Final calculation from Backend data:", {
        durationMinutes: currentDurationMinutes,
        durationHours: currentDurationMinutes / 60,
        rate: breakdown?.rate,
        quantity: breakdown?.quantity,
        rentalPrice,
        articlesPrice,
        total,
        breakdown,
      });
    } catch (error) {
      console.error("❌ Error calculating prices:", error);
      // Minimaler Fallback ohne komplexe Berechnungen
      setCalculation({
        rentalHours: 0,
        rentalPrice: 0,
        articlesPrice: 0,
        subtotal: 0,
        tax: 0,
        total: 0,
        isSmallBusiness: primaryCompany?.isSmallBusiness || false,
        vatRate: primaryCompany?.defaultVatRate || 19,
        currentDurationMinutes: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString("de-DE") +
      " " +
      date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const isHourlyArticle = (articleOrItem?: Article | RentalItem | null) => {
    const unit =
      (articleOrItem as RentalItem)?.unit ||
      (articleOrItem as RentalItem)?.article?.unit ||
      (articleOrItem as Article)?.unit ||
      "";
    return ["stunde", "tag", "woche"].includes(unit.trim().toLowerCase());
  };

  const getSelectableArticles = () => {
    const rentalItems = currentRental?.items || [];
    return availableArticles.filter((article) => {
      const existingItems = rentalItems.filter(
        (item) => item.articleId === article.id.toString()
      );

      if (isHourlyArticle(article)) {
        return !existingItems.some((item) => !item.endedAt);
      }

      return true;
    });
  };

  const getExistingArticleCount = (articleId: string) =>
    (currentRental?.items || []).filter((item) => item.articleId === articleId)
      .length;

  const getDefaultHourlyRange = () => ({
    startedAt: startDate || formatDateTimeLocal(new Date()),
    endedAt: "",
  });

  const updateRentalDates = async () => {
    if (!currentRental || !hasStartDateChanged) return;

    try {
      setLoading(true);
      // Nur Startzeit aktualisieren
      await apiClient.updateRental(currentRental.id, {
        startDate: new Date(startDate),
        // endDate wird NICHT gesetzt - Vermietung läuft weiter
      });

      // Lokale State aktualisieren statt neu laden
      setCurrentRental((prev) => ({
        ...prev,
        startDate: new Date(startDate),
      }));
      setHasStartDateChanged(false);

      // Nach der Startzeit-Aktualisierung Preise neu berechnen
      calculatePrices();
    } catch (error: any) {
      window.alert(`Fehler beim Aktualisieren: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Neue Funktion: Startzeit sofort speichern
  const handleStartDateChange = async (newStartDate: string) => {
    setStartDate(newStartDate);
    setHasStartDateChanged(true);

    // Automatisch speichern nach kurzer Verzögerung
    if (updateStartDateTimeoutRef.current) {
      clearTimeout(updateStartDateTimeoutRef.current);
    }

    updateStartDateTimeoutRef.current = setTimeout(async () => {
      if (!currentRental) return;

      try {
        await apiClient.updateRental(currentRental.id, {
          startDate: new Date(newStartDate),
        });

        // Lokale State aktualisieren
        setCurrentRental((prev) => ({
          ...prev,
          startDate: new Date(newStartDate),
        }));
        setHasStartDateChanged(false);

        // Preise neu berechnen
        calculatePrices();
      } catch (error: any) {
        console.error(
          "Fehler beim automatischen Speichern der Startzeit:",
          error
        );
        // Bei Fehler bleibt hasStartDateChanged true, damit beim Beenden nochmal versucht wird
      }
    }, 1000); // 1 Sekunde Verzögerung
  };

  const updateItemQuantity = async (itemId: string, newQuantity: number) => {
    if (!currentRental) return;

    if (newQuantity < 1) {
      await removeItem(itemId);
      return;
    }

    try {
      setLoading(true);
      await apiClient.updateRentalItem(currentRental.id, itemId, {
        quantity: newQuantity,
      });

      // Lade die aktuellen Rental-Daten neu, um sicherzustellen, dass alle Items korrekt angezeigt werden
      await loadCurrentRentalWithItems();
      setEditingItem(null);
    } catch (error: any) {
      window.alert(`Fehler beim Aktualisieren: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId: string) => {
    if (!currentRental) return;
    // Kein Confirm mehr - direktes Entfernen

    try {
      setLoading(true);
      await apiClient.removeItemFromRental(currentRental.id, itemId);

      // Lade die aktuellen Rental-Daten neu, um sicherzustellen, dass alle Items korrekt angezeigt werden
      await loadCurrentRentalWithItems();
    } catch (error: any) {
      window.alert(`Fehler beim Entfernen: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Bundle-Management-Funktionen
  const addBundleToRental = async (bundleId: string) => {
    if (!currentRental) return;

    try {
      setLoading(true);

      // Bundle zur Vermietung hinzufügen
      await apiClient.addBundleToRental(currentRental.id, bundleId);

      // Komplettes Rental-Objekt neu laden um Bundle-Informationen zu aktualisieren
      await loadCurrentRentalWithItems();
      setShowBundleSelection(false);

      console.log("✅ Bundle added successfully and rental reloaded");
    } catch (error: any) {
      console.error("❌ Error adding bundle:", error);
      window.alert(`Fehler beim Hinzufügen des Bundles: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeBundleFromRental = async () => {
    if (!currentRental || !currentRental.bundleId) return;

    if (!window.confirm("Möchten Sie das Bundle wirklich entfernen?")) return;

    try {
      setLoading(true);

      // Bundle von Vermietung entfernen
      await apiClient.removeBundleFromRental(currentRental.id);

      // Komplettes Rental-Objekt neu laden um sicherzustellen, dass bundleId=null und bundle=null
      await loadCurrentRentalWithItems();

      console.log("✅ Bundle removed successfully and rental reloaded");
    } catch (error: any) {
      console.error("❌ Error removing bundle:", error);
      window.alert(`Fehler beim Entfernen des Bundles: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addNewArticles = async () => {
    if (!currentRental) return;

    const articlesToAdd = Object.entries(selectedNewArticles).filter(
      ([_, quantity]) => quantity > 0
    );

    if (articlesToAdd.length === 0) return;

    try {
      setLoading(true);

      for (const [articleId, quantity] of articlesToAdd) {
        const article = availableArticles.find((item) => item.id === articleId);
        await apiClient.addItemToRental(currentRental.id, {
          articleId,
          quantity: isHourlyArticle(article) ? 1 : quantity,
          ...(isHourlyArticle(article)
            ? {
                startedAt: new Date(),
              }
            : {}),
        });
      }

      // Lade die aktuellen Rental-Daten neu, um sicherzustellen, dass alle Items korrekt angezeigt werden
      await loadCurrentRentalWithItems();
      setShowAddArticles(false);
      setSelectedNewArticles({});

      // Keine Alert-Benachrichtigung mehr
    } catch (error: any) {
      window.alert(`Fehler beim Hinzufügen: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Vermietung beenden - öffne nur die EndRentalModal
  const handleEndRental = async () => {
    console.log("🎯 handleEndRental called");

    if (!currentRental) {
      console.error("❌ No currentRental found");
      return;
    }

    console.log("🔍 Opening EndRentalModal for rental:", currentRental);

    // Setze endRentalResponse zurück und öffne Modal
    setEndRentalResponse(null);
    setShowEndRentalModal(true);
  };

  // Tatsächliche Beendigung der Vermietung - wird von EndRentalModal aufgerufen
  const performEndRental = async () => {
    console.log("🎯 performEndRental called - actually ending rental");

    if (!currentRental) {
      console.error("❌ No currentRental found");
      throw new Error("Keine aktuelle Vermietung verfügbar");
    }

    // Prüfe ob die Vermietung bereits beendet wurde
    if (currentRental.status === "COMPLETED") {
      console.log(
        "⚠️ Rental is already completed, trying to load existing invoice"
      );

      try {
        // Versuche existierende Rechnung zu laden
        const response = await apiClient.getInvoices();
        const invoices = (response as any)?.data || response || [];

        if (Array.isArray(invoices)) {
          const rentalInvoice = invoices.find(
            (inv: any) => inv.rentalId === currentRental.id
          );

          if (rentalInvoice && rentalInvoice.qrCode) {
            console.log("✅ Found existing invoice for completed rental");

            // Erstelle eine Response-ähnliche Struktur
            const fakeResponse = {
              rental: currentRental,
              invoice: rentalInvoice,
              hasInvoice: true,
              qrCode: rentalInvoice.qrCode,
              pdfDownloadUrl: `/api/invoices/public/${rentalInvoice.qrCode}/download`,
              invoiceViewUrl: `/pos/invoice/${rentalInvoice.qrCode}`,
            };

            setEndRentalResponse(fakeResponse);
            return fakeResponse;
          }
        }

        throw new Error(
          "Keine Rechnung für diese beendete Vermietung gefunden"
        );
      } catch (error) {
        console.error("❌ Error loading existing invoice:", error);
        throw new Error(
          "Vermietung bereits beendet, aber Rechnung konnte nicht geladen werden"
        );
      }
    }

    try {
      // Erst Startzeit speichern, falls geändert
      if (hasStartDateChanged) {
        console.log("🔄 Updating start date...");
        await apiClient.updateRental(currentRental.id, {
          startDate: new Date(startDate),
        });
        setHasStartDateChanged(false);
        console.log("✅ Start date updated");
      }

      // Endzeit aktualisieren falls geändert
      if (endDate && currentRental.endDate) {
        const currentEndTime = new Date(currentRental.endDate).getTime();
        const newEndTime = new Date(endDate).getTime();

        if (currentEndTime !== newEndTime) {
          console.log("🔄 Updating end date...");
          await apiClient.updateRental(currentRental.id, {
            endDate: new Date(endDate),
          });
          console.log("✅ End date updated");
        }
      }

      // Vermietung beenden mit korrekter API-Methode
      console.log("🔄 Ending rental...");
      const finalEndDate = endDate ? new Date(endDate) : new Date();
      console.log("🕐 Final endDate:", finalEndDate);

      // Verwende die neue endRentalWithInvoice Methode für erweiterte Response
      const response = await apiClient.endRentalWithInvoice(
        currentRental.id,
        finalEndDate
      );

      console.log("✅ Rental ended successfully:", response);

      // Speichere die Response für weitere Verwendung
      setEndRentalResponse(response);

      // NICHT onUpdate() aufrufen - das würde die InvoiceModal schließen
      // onUpdate(); // Entfernt - würde die Modal schließen

      console.log("💾 Response saved for EndRentalModal to use");

      // Gebe die Response an EndRentalModal zurück
      return response;
    } catch (error: any) {
      console.error("❌ Error ending rental:", error);
      throw new Error(error.message || "Fehler beim Beenden der Vermietung");
    }
  };

  const handleArticleQuantityChange = (articleId: string, quantity: number) => {
    setSelectedNewArticles((prev) => ({
      ...prev,
      [articleId]: Math.max(0, quantity),
    }));
  };

  const openTimeItemEditor = (item: RentalItem) => {
    const fallback = getDefaultHourlyRange();
    setEditingTimeItem(item);
    setEditingTimeRange({
      startedAt: item.startedAt
        ? formatDateTimeLocal(new Date(item.startedAt))
        : fallback.startedAt,
      endedAt: item.endedAt
        ? formatDateTimeLocal(new Date(item.endedAt))
        : fallback.endedAt,
    });
  };

  const saveTimeItemEditor = async () => {
    if (!currentRental || !editingTimeItem) return;

    try {
      setLoading(true);
      await apiClient.updateRentalItem(currentRental.id, editingTimeItem.id, {
        startedAt: new Date(editingTimeRange.startedAt),
        ...(editingTimeRange.endedAt
          ? { endedAt: new Date(editingTimeRange.endedAt) }
          : {}),
      });
      setEditingTimeItem(null);
      await loadCurrentRentalWithItems();
    } catch (error: any) {
      window.alert(`Fehler beim Speichern der Zeiten: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const finishHourlyItem = async (item: RentalItem) => {
    if (!currentRental) return;

    try {
      setLoading(true);
      await apiClient.updateRentalItem(currentRental.id, item.id, {
        endedAt: new Date(),
      });
      await loadCurrentRentalWithItems();
    } catch (error: any) {
      window.alert(`Fehler beim Beenden des Artikels: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for mobile optimization
  const getTotalArticleCount = () => {
    return Object.values(selectedNewArticles).reduce(
      (sum, qty) => sum + qty,
      0
    );
  };

  const hasUnsavedChanges = () => {
    return hasStartDateChanged || getTotalArticleCount() > 0;
  };

  const getMobileSteps = (): MobileStep[] => [
    {
      id: "summary",
      label: "Übersicht",
      icon: <CalculateIcon />,
      completed: calculation !== null,
    },
    {
      id: "time",
      label: "Mietzeitraum",
      icon: <AccessTimeIcon />,
      completed: !hasStartDateChanged,
      hasChanges: hasStartDateChanged,
    },
    {
      id: "bundle",
      label: "Bundle",
      icon: <BusinessIcon />,
      completed: true,
    },
    {
      id: "items",
      label: "Artikel",
      icon: <InventoryIcon />,
      completed: getTotalArticleCount() === 0,
      hasChanges: getTotalArticleCount() > 0,
    },
  ];

  // Mobile Tab Component - Now used for both Mobile and Desktop
  const MobileTabBar = () => (
    <Paper
      elevation={3}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1300,
        borderRadius: 0,
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        sx={{
          overflow: "auto",
          width: "100%",
          "&::-webkit-scrollbar": {
            display: "none", // Hide scrollbar for cleaner look
          },
          scrollbarWidth: "none", // Firefox
        }}
      >
        {MOBILE_TABS.map((tab) => {
          const step = getMobileSteps().find((s) => s.id === tab.id);
          return (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? "contained" : "text"}
              startIcon={
                step?.hasChanges ? (
                  <Badge color="warning" variant="dot">
                    {tab.icon}
                  </Badge>
                ) : (
                  tab.icon
                )
              }
              sx={{
                minWidth: 0, // Entfernt minWidth constraint
                flex: "1 1 0", // Gleiche Breite für alle Tabs
                borderRadius: 0,
                py: isMobile ? 2 : 2.5,
                px: isMobile ? 1 : 2, // Mehr Padding auf Desktop
                fontSize: isMobile ? "0.75rem" : "0.9rem", // Größere Schrift auf Desktop
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "25%", // Stellt sicher, dass 4 Tabs in eine Zeile passen
                minHeight: isMobile ? 48 : 56, // Höhere Buttons auf Desktop
              }}
            >
              {tab.label}
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );

  // Render Time Period Section - Optimized for Mobile & Desktop
  const renderTimePeriod = () => (
    <Fade in={activeTab === "time"}>
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          display: activeTab === "time" ? "block" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h6"}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
              <AccessTimeIcon sx={{ fontSize: "1.2rem" }} />
            </Avatar>
            Mietzeitraum
          </Typography>
          {hasStartDateChanged && (
            <Chip
              icon={<WarningIcon />}
              label="Änderungen"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mietbeginn"
              type="datetime-local"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              variant="outlined"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: isMobile ? "1rem" : "0.875rem",
                  height: isMobile ? 56 : "auto",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mietende (Vorschau)"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              variant="outlined"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: isMobile ? "1rem" : "0.875rem",
                  height: isMobile ? 56 : "auto",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {calculation && (
          <Zoom in={!!calculation}>
            <Paper
              elevation={isMobile ? 1 : 2}
              sx={{
                mt: 3,
                p: isMobile ? 2 : 3,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon color="primary" fontSize="small" />
                    <Typography
                      variant={isMobile ? "body1" : "body2"}
                      sx={{ fontWeight: 600 }}
                    >
                      Dauer:
                    </Typography>
                    <Typography variant={isMobile ? "body1" : "body2"}>
                      {calculation.timeUnit === "Stunden"
                        ? `${calculation.billedQuantity?.toFixed(1) || 0} Std`
                        : calculation.timeUnit === "Tage"
                        ? `${calculation.billedQuantity?.toFixed(1) || 0} Tage`
                        : `${Math.round(
                            calculation.currentDurationMinutes || 0
                          )} Min`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EuroIcon color="primary" fontSize="small" />
                    <Typography
                      variant={isMobile ? "body1" : "body2"}
                      sx={{ fontWeight: 600 }}
                    >
                      Rate:
                    </Typography>
                    <Typography variant={isMobile ? "body1" : "body2"}>
                      {calculation.effectiveRate && calculation.timeUnit
                        ? `${calculation.effectiveRate}€/${
                            calculation.timeUnit === "Stunden"
                              ? "Std"
                              : calculation.timeUnit === "Tage"
                              ? "Tag"
                              : "Min"
                          }`
                        : "N/A"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {calculation.wasMinimumApplied && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    fontSize: isMobile ? "0.9rem" : "0.875rem",
                    "& .MuiAlert-icon": {
                      fontSize: isMobile ? "1.2rem" : "1rem",
                    },
                  }}
                  icon={<WarningIcon />}
                >
                  Mindestmietdauer von {calculation.minimumRentalDuration}{" "}
                  {calculation.timeUnit === "Stunden"
                    ? "Std"
                    : calculation.timeUnit === "Tage"
                    ? "Tage"
                    : "Min"}{" "}
                  angewendet
                </Alert>
              )}

              {hasStartDateChanged && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    fontSize: isMobile ? "0.9rem" : "0.875rem",
                  }}
                  icon={<InfoIcon />}
                >
                  Startzeit wird automatisch gespeichert...
                </Alert>
              )}
            </Paper>
          </Zoom>
        )}
      </Box>
    </Fade>
  );

  // Render Bundle Section - Mobile Optimized
  const renderBundleSection = () => (
    <Fade in={activeTab === "bundle"}>
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          display: activeTab === "bundle" ? "block" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h6"}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
              <BusinessIcon sx={{ fontSize: "1.2rem" }} />
            </Avatar>
            Bundle
          </Typography>

          {!currentRental?.bundleId ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowBundleSelection(true)}
              disabled={loading}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: isMobile ? 48 : "auto",
                borderRadius: isMobile ? 3 : 1,
              }}
            >
              Bundle hinzufügen
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<RemoveIcon />}
              onClick={removeBundleFromRental}
              disabled={loading}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: isMobile ? 48 : "auto",
                borderRadius: isMobile ? 3 : 1,
              }}
            >
              Bundle entfernen
            </Button>
          )}
        </Box>

        {currentRental?.bundleId && currentRental?.bundle ? (
          <Zoom in={!!currentRental.bundle}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: isMobile ? 1 : 2,
                border: "2px solid",
                borderColor: "primary.light",
                bgcolor: "primary.50",
              }}
            >
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <Avatar
                    sx={{ bgcolor: "primary.main", width: 40, height: 40 }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant={isMobile ? "h6" : "subtitle1"}
                      sx={{ fontWeight: 600 }}
                    >
                      {currentRental.bundle.article?.name || "Bundle"}
                    </Typography>
                    <Typography
                      variant={isMobile ? "body1" : "body2"}
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {currentRental.bundle.bundlePrice}€ /{" "}
                      {currentRental.rentalUnit === "HOURS"
                        ? "Stunde"
                        : currentRental.rentalUnit}
                    </Typography>
                  </Box>
                </Box>

                {currentRental.bundle.notes && (
                  <Typography
                    variant={isMobile ? "body2" : "caption"}
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      fontStyle: "italic",
                    }}
                  >
                    {currentRental.bundle.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Zoom>
        ) : (
          <Paper
            sx={{
              p: isMobile ? 3 : 4,
              textAlign: "center",
              bgcolor: "grey.50",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
            }}
          >
            <BusinessIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
            <Typography
              variant={isMobile ? "body1" : "body2"}
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Kein Bundle ausgewählt
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Fügen Sie ein Bundle hinzu für optimierte Preise
            </Typography>
          </Paper>
        )}
      </Box>
    </Fade>
  );

  // Render Items Section - Touch Optimized
  const renderItemsSection = () => (
    <Fade in={activeTab === "items"}>
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          display: activeTab === "items" ? "block" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h6"}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
              <InventoryIcon sx={{ fontSize: "1.2rem" }} />
            </Avatar>
            Zusätzliche Artikel
            {currentRental?.items && currentRental.items.length > 0 && (
              <Chip
                label={currentRental.items.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddArticles(true)}
            disabled={loading}
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : "auto",
              borderRadius: isMobile ? 3 : 1,
            }}
          >
            Artikel hinzufügen
          </Button>
        </Box>

        {currentRental?.items && currentRental.items.length > 0 ? (
          <Stack spacing={isMobile ? 2 : 1}>
            {currentRental.items.map((item: RentalItem) => (
              <Zoom key={item.id} in={true}>
                {(() => {
                  const isRunningHourlyItem =
                    isHourlyArticle(item) && !item.endedAt;

                  return (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: editingItem === item.id ? "2px solid" : "1px solid",
                    borderColor:
                      editingItem === item.id ? "primary.main" : "grey.300",
                    bgcolor: editingItem === item.id ? "primary.50" : "white",
                    transition: "all 0.2s ease-in-out",
                    minHeight: isMobile ? 120 : "auto",
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: "primary.light",
                    },
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? 2 : 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant={isMobile ? "h6" : "subtitle1"}
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {item.article?.name || item.description || "Artikel"}
                        </Typography>
                        <Typography
                          variant={isMobile ? "body1" : "body2"}
                          color="text.secondary"
                        >
                          {item.unitPrice}€ / {item.unit || "Stück"}
                        </Typography>
                        {isHourlyArticle(item) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {item.startedAt && item.endedAt
                              ? `${formatDate(
                                  item.startedAt.toString()
                                )} - ${formatDate(item.endedAt.toString())}`
                              : item.startedAt
                              ? `Läuft seit ${formatDate(
                                  item.startedAt.toString()
                                )}`
                              : "Läuft seit dem Hinzufügen"}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: isMobile ? "100%" : "auto",
                          justifyContent: isMobile
                            ? "space-between"
                            : "flex-end",
                        }}
                      >
                        {editingItem === item.id && !isRunningHourlyItem ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: isMobile ? 1 : "none",
                            }}
                          >
                            <TextField
                              size={isMobile ? "medium" : "small"}
                              type="number"
                              inputProps={{ min: 1 }}
                              defaultValue={item.quantity}
                              onBlur={(e) =>
                                updateItemQuantity(
                                  item.id,
                                  parseInt(e.target.value)
                                )
                              }
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  updateItemQuantity(
                                    item.id,
                                    parseInt(
                                      (e.target as HTMLInputElement).value
                                    )
                                  );
                                }
                              }}
                              autoFocus
                              sx={{
                                width: isMobile ? 100 : 80,
                                "& .MuiInputBase-root": {
                                  height: isMobile ? 48 : "auto",
                                },
                              }}
                            />
                            <Tooltip title="Speichern">
                              <IconButton
                                size={isMobile ? "large" : "small"}
                                onClick={() => setEditingItem(null)}
                                color="primary"
                                sx={{
                                  minWidth: isMobile ? 48 : "auto",
                                  minHeight: isMobile ? 48 : "auto",
                                }}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: isMobile ? 2 : 1,
                              width: isMobile ? "100%" : "auto",
                              justifyContent: isMobile
                                ? "space-between"
                                : "flex-end",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {isRunningHourlyItem ? (
                                <Tooltip title="Menge wird nach dem Beenden berechnet">
                                  <Chip
                                    label={`${item.quantity} ${
                                      item.unit || "x"
                                    }`}
                                    size={isMobile ? "medium" : "small"}
                                    icon={<AccessTimeIcon />}
                                    color="warning"
                                    variant="outlined"
                                    sx={{
                                      height: isMobile ? 40 : "auto",
                                      fontSize: isMobile ? "0.9rem" : "0.8rem",
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Menge bearbeiten">
                                <Chip
                                  label={`${item.quantity} ${item.unit || "x"}`}
                                  size={isMobile ? "medium" : "small"}
                                  onClick={() => setEditingItem(item.id)}
                                  clickable
                                  icon={<TouchAppIcon />}
                                  color="primary"
                                  variant="outlined"
                                  sx={{
                                    "&:hover": { bgcolor: "primary.50" },
                                    height: isMobile ? 40 : "auto",
                                    fontSize: isMobile ? "0.9rem" : "0.8rem",
                                  }}
                                />
                              </Tooltip>
                              )}
                              {isHourlyArticle(item) && (
                                <Tooltip title="Start und Ende bearbeiten">
                                  <IconButton
                                    size={isMobile ? "large" : "small"}
                                    onClick={() => openTimeItemEditor(item)}
                                    color="primary"
                                    disabled={loading}
                                    sx={{
                                      minWidth: isMobile ? 48 : "auto",
                                      minHeight: isMobile ? 48 : "auto",
                                    }}
                                  >
                                    <AccessTimeIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {isHourlyArticle(item) && !item.endedAt && (
                                <Button
                                  size={isMobile ? "medium" : "small"}
                                  variant="contained"
                                  color="success"
                                  onClick={() => finishHourlyItem(item)}
                                  disabled={loading}
                                  startIcon={<CheckIcon />}
                                  sx={{
                                    minHeight: isMobile ? 40 : "auto",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Beenden
                                </Button>
                              )}
                              <Typography
                                variant={isMobile ? "h6" : "body2"}
                                sx={{
                                  minWidth: isMobile ? 80 : 60,
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: "primary.main",
                                }}
                              >
                                {(item.unitPrice * item.quantity).toFixed(2)}€
                              </Typography>
                            </Box>
                            <Tooltip title="Artikel entfernen">
                              <IconButton
                                size={isMobile ? "large" : "small"}
                                onClick={() => removeItem(item.id)}
                                color="error"
                                disabled={loading}
                                sx={{
                                  minWidth: isMobile ? 48 : "auto",
                                  minHeight: isMobile ? 48 : "auto",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                  );
                })()}
              </Zoom>
            ))}
          </Stack>
        ) : calculation && calculation.articlesPrice > 0 ? (
          <Alert
            severity="info"
            sx={{
              fontSize: isMobile ? "0.9rem" : "0.875rem",
              "& .MuiAlert-icon": { fontSize: isMobile ? "1.5rem" : "1.2rem" },
            }}
          >
            Artikel vorhanden, Details werden geladen... (Gesamtwert:{" "}
            {calculation.articlesPrice.toFixed(2)}€)
          </Alert>
        ) : (
          <Paper
            sx={{
              p: isMobile ? 3 : 4,
              textAlign: "center",
              bgcolor: "grey.50",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
            }}
          >
            <InventoryIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
            <Typography
              variant={isMobile ? "body1" : "body2"}
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Keine zusätzlichen Artikel
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Artikel hinzufügen für zusätzliche Leistungen
            </Typography>
          </Paper>
        )}
      </Box>
    </Fade>
  );

  // Render Price Summary Section - Enhanced for Mobile
  const renderPriceSummary = () => (
    <Fade in={activeTab === "summary"}>
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          display: activeTab === "summary" ? "block" : "none",
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h6"}
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
          }}
        >
          <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
            <CalculateIcon sx={{ fontSize: "1.2rem" }} />
          </Avatar>
          Preisberechnung
        </Typography>

        {calculation ? (
          <Paper
            variant="outlined"
            sx={{
              p: isMobile ? 3 : 2,
              borderRadius: 2,
              boxShadow: isMobile ? 2 : 1,
              bgcolor: "background.paper",
            }}
          >
            <Stack spacing={isMobile ? 2 : 1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <Box>
                  <Typography
                    variant={isMobile ? "body1" : "body2"}
                    sx={{ fontWeight: 500 }}
                  ></Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    {calculation.timeUnit === "Stunden"
                      ? `${calculation.billedQuantity?.toFixed(1) || 0} Std × ${
                          calculation.effectiveRate || 0
                        }€`
                      : calculation.timeUnit === "Tage"
                      ? `${
                          calculation.billedQuantity?.toFixed(1) || 0
                        } Tage × ${calculation.effectiveRate || 0}€`
                      : `${Math.round(
                          calculation.currentDurationMinutes || 0
                        )} Min × ${(
                          (calculation.effectiveRate || 0) / 60
                        ).toFixed(3)}€`}
                  </Typography>
                </Box>
                <Typography
                  variant={isMobile ? "h6" : "body2"}
                  sx={{ fontWeight: 600 }}
                >
                  {calculation.rentalPrice.toFixed(2)}€
                </Typography>
              </Box>

              {calculation.wasMinimumApplied && (
                <Alert
                  severity="warning"
                  sx={{
                    fontSize: isMobile ? "0.9rem" : "0.8rem",
                    "& .MuiAlert-message": { py: 0 },
                  }}
                  icon={
                    <WarningIcon fontSize={isMobile ? "medium" : "small"} />
                  }
                >
                  Mindestmietdauer von {calculation.minimumRentalDuration}{" "}
                  {calculation.timeUnit === "Stunden"
                    ? "Stunden"
                    : calculation.timeUnit === "Tage"
                    ? "Tagen"
                    : "Minuten"}{" "}
                  angewendet
                </Alert>
              )}

              {calculation.articlesPrice > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Typography
                    variant={isMobile ? "body1" : "body2"}
                    sx={{ fontWeight: 500 }}
                  >
                    Zusätzliche Artikel
                  </Typography>
                  <Typography
                    variant={isMobile ? "h6" : "body2"}
                    sx={{ fontWeight: 600 }}
                  >
                    {calculation.articlesPrice.toFixed(2)}€
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: isMobile ? 2 : 1 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <Typography
                  variant={isMobile ? "h6" : "body2"}
                  sx={{ fontWeight: 600 }}
                >
                  {calculation.isSmallBusiness
                    ? "Gesamtsumme:"
                    : "Zwischensumme :"}
                </Typography>
                <Typography
                  variant={isMobile ? "h6" : "body2"}
                  sx={{ fontWeight: 600 }}
                >
                  {calculation.subtotal.toFixed(2)}€
                </Typography>
              </Box>

              {!calculation.isSmallBusiness && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Typography
                    variant={isMobile ? "body1" : "body2"}
                    sx={{ fontWeight: 500 }}
                  >
                    MwSt. ({calculation.vatRate}%)
                  </Typography>
                  <Typography
                    variant={isMobile ? "h6" : "body2"}
                    sx={{ fontWeight: 600 }}
                  >
                    {calculation.tax.toFixed(2)}€
                  </Typography>
                </Box>
              )}

              {!calculation.isSmallBusiness && (
                <>
                  <Divider sx={{ my: isMobile ? 2 : 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: isMobile ? 2 : 1,
                      bgcolor: "primary.50",
                      mx: -2,
                      px: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h5" : "h6"}
                      sx={{ fontWeight: 700 }}
                    >
                      Gesamtsumme:
                    </Typography>
                    <Typography
                      variant={isMobile ? "h4" : "h6"}
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        textShadow: isMobile
                          ? "0 1px 2px rgba(0,0,0,0.1)"
                          : "none",
                      }}
                    >
                      {calculation.total.toFixed(2)}€
                    </Typography>
                  </Box>
                </>
              )}

              {calculation.isSmallBusiness && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 1,
                    fontSize: isMobile ? "0.9rem" : "0.8rem",
                    "& .MuiAlert-message": { py: 0 },
                  }}
                  icon={<InfoIcon fontSize={isMobile ? "medium" : "small"} />}
                >
                  Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.
                </Alert>
              )}
            </Stack>
          </Paper>
        ) : (
          <Paper
            sx={{
              p: isMobile ? 4 : 3,
              textAlign: "center",
              bgcolor: "grey.50",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Preise werden berechnet...
            </Typography>
          </Paper>
        )}
      </Box>
    </Fade>
  );

  // Main Render
  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={isMobile ? "lg" : "md"}
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
      PaperProps={{
        sx: {
          height: fullScreen ? "98vh" : isMobile ? "98vh" : "85vh",
          maxHeight: fullScreen ? "98vh" : isMobile ? "98vh" : "85vh",
          borderRadius: fullScreen ? 0 : 2,
          width: isMobile ? "100%" : "90vw",
          maxWidth: isMobile ? "lg" : "1000px",
        },
      }}
    >
      {/* Enhanced Header with Mobile Optimization */}
      <DialogTitle
        sx={{
          background: isMobile
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          p: isMobile ? 2 : 3,
          position: "sticky",
          top: 0,
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 1.5 : 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: isMobile ? 40 : 32,
                height: isMobile ? 40 : 32,
              }}
            >
              <ReceiptIcon sx={{ fontSize: isMobile ? "1.5rem" : "1.2rem" }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant={isMobile ? "h5" : "h6"}
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                Vermietung beenden
              </Typography>
              {isMobile && (
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    fontSize: "0.85rem",
                  }}
                >
                  Vorschau & Bearbeitung
                </Typography>
              )}
            </Box>
          </Box>

          {hasUnsavedChanges() && (
            <Chip
              icon={<WarningIcon />}
              label="Änderungen"
              size="small"
              sx={{
                bgcolor: "warning.main",
                color: "warning.contrastText",
                display: isMobile ? "none" : "flex",
              }}
            />
          )}

          <Tooltip title="Schließen">
            <IconButton
              onClick={onClose}
              sx={{
                color: "white",
                width: isMobile ? 48 : 40,
                height: isMobile ? 48 : 40,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
              disabled={loading}
            >
              <CloseIcon sx={{ fontSize: isMobile ? "1.5rem" : "1.2rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        {/* Tab Navigation for both Mobile and Desktop */}
        <MobileTabBar />

        {/* Content Sections */}
        <Box
          sx={{
            height: isMobile ? "calc(100vh - 200px)" : "calc(90vh - 200px)",
            overflow: "auto",
            p: isMobile ? 0 : 3,
          }}
        >
          {renderTimePeriod()}
          {renderBundleSection()}
          {renderItemsSection()}
          {renderPriceSummary()}
        </Box>
      </DialogContent>

      {/* Enhanced Action Bar */}
      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          gap: isMobile ? 1 : 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "sticky",
          bottom: 0,
          zIndex: 1300,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          size={isMobile ? "large" : "medium"}
          sx={{
            flex: isMobile ? 1 : "none",
            minHeight: isMobile ? 48 : "auto",
            borderRadius: isMobile ? 3 : 1,
          }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleEndRental}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
          size={isMobile ? "large" : "medium"}
          sx={{
            flex: isMobile ? 2 : "none",
            minHeight: isMobile ? 48 : "auto",
            borderRadius: isMobile ? 3 : 1,
            fontWeight: 600,
            boxShadow: isMobile ? 3 : 1,
          }}
        >
          {loading ? "Wird bearbeitet..." : "Vermietung beenden"}
        </Button>
      </DialogActions>

      {/* Enhanced Add Articles Modal - Mobile Optimized */}
      <Dialog
        open={showAddArticles}
        onClose={() => setShowAddArticles(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? "100vh" : "auto",
            maxHeight: isMobile ? "none" : "80vh",
            borderRadius: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            p: isMobile ? 2 : 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 32, height: 32 }}
            >
              <AddIcon />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h6"}
              sx={{ fontWeight: 600 }}
            >
              Artikel hinzufügen
            </Typography>
          </Box>
          {isMobile && (
            <IconButton
              onClick={() => setShowAddArticles(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
          {/* Quick Add Summary */}
          {getTotalArticleCount() > 0 && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: "primary.200",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Ausgewählte Artikel: {getTotalArticleCount()}
                </Typography>
                <Chip
                  label={`${getTotalArticleCount()} Artikel`}
                  color="primary"
                  size="small"
                />
              </Box>
            </Paper>
          )}

          <Grid container spacing={isMobile ? 1 : 2} sx={{ mt: 1 }}>
            {getSelectableArticles().length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Alle verfügbaren Artikel wurden dieser Vermietung bereits
                  hinzugefügt.
                </Alert>
              </Grid>
            )}
            {getSelectableArticles().map((article) => {
              const quantity = selectedNewArticles[article.id.toString()] || 0;
              const hourly = isHourlyArticle(article);
              const articleId = article.id.toString();
              const existingCount = getExistingArticleCount(articleId);
              return (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      border: quantity > 0 ? "2px solid" : "1px solid",
                      borderColor: quantity > 0 ? "primary.main" : "grey.300",
                      bgcolor: quantity > 0 ? "primary.50" : "white",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: 2,
                        borderColor: "primary.light",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: isMobile ? 2 : 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ flex: 1, mb: 2 }}>
                        <Typography
                          variant={isMobile ? "subtitle1" : "subtitle2"}
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {article.name}
                        </Typography>
                        <Typography
                          variant={isMobile ? "h6" : "body2"}
                          color="primary.main"
                          sx={{ fontWeight: 600 }}
                        >
                          {article.price}€ / {article.unit || "Stück"}
                        </Typography>
                        {existingCount > 0 && (
                          <Chip
                            label={`${existingCount}x bereits auf der Rechnung`}
                            color="info"
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>

                      {hourly ? (
                        <Stack spacing={1.5} sx={{ mt: "auto" }}>
                          <Button
                            variant={quantity > 0 ? "contained" : "outlined"}
                            startIcon={<AccessTimeIcon />}
                            onClick={() => {
                              handleArticleQuantityChange(
                                articleId,
                                quantity > 0 ? 0 : 1
                              );
                            }}
                          >
                            {quantity > 0 ? "Ausgewählt" : "Auswählen"}
                          </Button>
                          {quantity > 0 && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              Start: jetzt. Der Artikel läuft, bis er im
                              Artikel-Tab beendet wird.
                            </Alert>
                          )}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: isMobile ? 2 : 1,
                            mt: "auto",
                          }}
                        >
                          <Tooltip title="Weniger">
                            <span>
                              <IconButton
                                size={isMobile ? "large" : "medium"}
                                onClick={() =>
                                  handleArticleQuantityChange(
                                    article.id.toString(),
                                    quantity - 1
                                  )
                                }
                                disabled={quantity <= 0}
                                sx={{
                                  bgcolor:
                                    quantity > 0 ? "primary.main" : "grey.300",
                                  color: quantity > 0 ? "white" : "grey.500",
                                  "&:hover": {
                                    bgcolor:
                                      quantity > 0
                                        ? "primary.dark"
                                        : "grey.400",
                                  },
                                  width: isMobile ? 48 : 36,
                                  height: isMobile ? 48 : 36,
                                }}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Typography
                            variant={isMobile ? "h5" : "h6"}
                            sx={{
                              minWidth: isMobile ? 60 : 40,
                              textAlign: "center",
                              fontWeight: 600,
                              fontSize: isMobile ? "1.5rem" : "1.2rem",
                            }}
                          >
                            {quantity}
                          </Typography>

                          <Tooltip title="Mehr">
                            <IconButton
                              size={isMobile ? "large" : "medium"}
                              onClick={() =>
                                handleArticleQuantityChange(
                                  article.id.toString(),
                                  quantity + 1
                                )
                              }
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                "&:hover": { bgcolor: "primary.dark" },
                                width: isMobile ? 48 : 36,
                                height: isMobile ? 48 : 36,
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}

                      {quantity > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid",
                            borderColor: "primary.200",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant={isMobile ? "subtitle1" : "body2"}
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                            }}
                          >
                            Summe: {(article.price * quantity).toFixed(2)}€
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: isMobile ? 2 : 3,
            gap: isMobile ? 1 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setShowAddArticles(false)}
            variant="outlined"
            size={isMobile ? "large" : "medium"}
            sx={{
              flex: isMobile ? 1 : "none",
              minHeight: isMobile ? 48 : "auto",
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={addNewArticles}
            disabled={
              Object.values(selectedNewArticles).every((q) => q === 0) ||
              loading
            }
            variant="contained"
            size={isMobile ? "large" : "medium"}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{
              flex: isMobile ? 2 : "none",
              minHeight: isMobile ? 48 : "auto",
              fontWeight: 600,
            }}
          >
            {loading
              ? "Wird hinzugefügt..."
              : getTotalArticleCount() > 0
              ? `${getTotalArticleCount()} Artikel hinzufügen`
              : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingTimeItem}
        onClose={() => setEditingTimeItem(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Zeitspanne bearbeiten</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {editingTimeItem?.article?.name ||
                editingTimeItem?.description ||
                "Stundenartikel"}
            </Typography>
            <TextField
              fullWidth
              label="Start"
              type="datetime-local"
              value={editingTimeRange.startedAt}
              onChange={(event) =>
                setEditingTimeRange((prev) => ({
                  ...prev,
                  startedAt: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Ende"
              type="datetime-local"
              value={editingTimeRange.endedAt}
              onChange={(event) =>
                setEditingTimeRange((prev) => ({
                  ...prev,
                  endedAt: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            {editingTimeRange.startedAt && editingTimeRange.endedAt && (
              <Alert severity="info">
                Abgerechnet wird auf die nächste volle Stunde. Die ermittelte
                Dauer wird als Menge gespeichert und bleibt anschließend wie
                jede andere Menge bearbeitbar.
              </Alert>
            )}
            {editingTimeRange.startedAt && !editingTimeRange.endedAt && (
              <Alert severity="info">
                Ohne Ende läuft der Artikel weiter. Beendet wird er über die
                Beenden-Aktion im Artikel-Tab.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTimeItem(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={saveTimeItemEditor}
            disabled={loading || !editingTimeRange.startedAt}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Bundle Selection Modal */}
      <Dialog
        open={showBundleSelection}
        onClose={() => setShowBundleSelection(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? "100vh" : "auto",
            maxHeight: isMobile ? "none" : "80vh",
            borderRadius: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            p: isMobile ? 2 : 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 32, height: 32 }}
            >
              <BusinessIcon />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h6"}
              sx={{ fontWeight: 600 }}
            >
              Bundle auswählen
            </Typography>
          </Box>
          {isMobile && (
            <IconButton
              onClick={() => setShowBundleSelection(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
          {availableBundles.length > 0 ? (
            <Grid container spacing={isMobile ? 1 : 2} sx={{ mt: 1 }}>
              {availableBundles.map((bundle) => (
                <Grid item xs={12} sm={6} key={bundle.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      transition: "all 0.2s ease-in-out",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: 3,
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={() => addBundleToRental(bundle.id)}
                  >
                    <CardContent
                      sx={{
                        p: isMobile ? 3 : 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 40,
                            height: 40,
                          }}
                        >
                          <BusinessIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant={isMobile ? "h6" : "subtitle1"}
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {bundle.article?.name || "Bundle"}
                          </Typography>
                          <Typography
                            variant={isMobile ? "h6" : "body2"}
                            color="primary.main"
                            sx={{ fontWeight: 600 }}
                          >
                            {bundle.bundlePrice}€ / pro Mieteinheit
                          </Typography>
                        </Box>
                      </Box>

                      {bundle.notes && (
                        <Typography
                          variant={isMobile ? "body2" : "caption"}
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            fontStyle: "italic",
                            flex: 1,
                          }}
                        >
                          {bundle.notes}
                        </Typography>
                      )}

                      <Button
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckIcon />
                          )
                        }
                        size={isMobile ? "large" : "medium"}
                        sx={{
                          mt: "auto",
                          minHeight: isMobile ? 48 : "auto",
                          fontWeight: 600,
                        }}
                      >
                        {loading ? "Wird hinzugefügt..." : "Auswählen"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              sx={{
                p: isMobile ? 4 : 6,
                textAlign: "center",
                bgcolor: "grey.50",
                border: "2px dashed",
                borderColor: "grey.300",
                borderRadius: 2,
                mt: 2,
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography
                variant={isMobile ? "h6" : "body1"}
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Keine Bundles verfügbar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Für dieses Objekt sind derzeit keine Bundles konfiguriert
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: isMobile ? 2 : 3,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setShowBundleSelection(false)}
            variant="outlined"
            size={isMobile ? "large" : "medium"}
            sx={{
              flex: isMobile ? 1 : "none",
              minHeight: isMobile ? 48 : "auto",
            }}
          >
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Rental Modal - Enhanced */}
      <EndRentalModal
        open={showEndRentalModal}
        rental={currentRental}
        onClose={() => {
          console.log(
            "🔚 EndRentalModal closing - user finished with rental end process"
          );
          setShowEndRentalModal(false);
          onClose();
        }}
        onConfirm={performEndRental}
        calculation={calculation}
        endRentalResponse={endRentalResponse}
      />

      {/* Mobile Quick Actions FAB */}
      {isMobile && !showEndRentalModal && (
        <Zoom in={hasUnsavedChanges()}>
          <Fab
            color="warning"
            sx={{
              position: "fixed",
              bottom: 80,
              right: 16,
              zIndex: 1400,
            }}
            onClick={() => setShowMobileActions(true)}
          >
            <Badge badgeContent={getTotalArticleCount()} color="error">
              <WarningIcon />
            </Badge>
          </Fab>
        </Zoom>
      )}

      {/* Mobile Actions Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={showMobileActions}
        onClose={() => setShowMobileActions(false)}
        onOpen={() => setShowMobileActions(true)}
        sx={{
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "50vh",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            Ungespeicherte Änderungen
          </Typography>

          {hasStartDateChanged && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Startzeit wurde geändert und wird automatisch gespeichert
            </Alert>
          )}

          {getTotalArticleCount() > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {getTotalArticleCount()} Artikel zum Hinzufügen ausgewählt
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => setShowMobileActions(false)}
            size="large"
            sx={{ mt: 2 }}
          >
            Verstanden
          </Button>
        </Box>
      </SwipeableDrawer>
    </Dialog>
  );
};

export default InvoiceModal;
